import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useTimezoneSelect } from 'react-timezone-select';
import Button from '../controls/Button';
import { INDUSTRIES, RHFPatterns, TIMEZONES } from '../constants/global-constants';
import { useData } from '../context/data.context';
import CustomInputBox from '../controls/CustomInputBox';
import CustomDropdown from '../controls/CustomDropdown';
import { saverUserUnitSettings, updateClient } from '../services/dashboard-service';

const labelStyle = 'original';

type FORMDATA = {
  clientName: string;
  orgUrl: string;
  timeZone: string;
  industry: string;
};

/**
 * Organisation component to edit the current organisation.
 * @returns  A React element representing the Organisation component.
 */
export default function Organisation(): ReactElement {
  const { clientData: currentClient, userConfigData } = useData();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { options, parseTimezone } = useTimezoneSelect({ labelStyle, timezones: TIMEZONES });
  const [loading, setLoading] = useState(false);

  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: useMemo(() => {
      return {
        clientName: currentClient?.clientName || '',
        timeZone: userConfigData.timeZone || '',
        orgUrl: currentClient?.orgUrl || ''
      };
    }, [userConfigData, currentClient])
  });

  const { handleSubmit, reset, watch } = methods;

  useEffect(() => {
    reset({
      clientName: currentClient?.clientName || '',
      timeZone: userConfigData.timeZone || '',
      orgUrl: currentClient?.orgUrl || '',
      industry: currentClient?.industry || ''
    });
  }, [currentClient]);

  /**
   * Handles changes in dropdown input values to select group.
   * @param data - The event object representing the group input change.
   */
  const handleUpdate = async (data: FORMDATA): Promise<void> => {
    try {
      setLoading(true);
      await saverUserUnitSettings({
        ...userConfigData,
        timeZone: data.timeZone
      });
      if (currentClient?.id) {
        await updateClient(
          {
            id: currentClient.id,
            plan: currentClient.plan,
            ...data
          },
          currentClient.id
        );
      }
      setLoading(false);
      toast.success('Updated successfully');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setLoading(false);
    }
  };

  const updatedTimeZones = options
    .map((timeZone) => {
      const zone = timeZone;
      const labelParts = timeZone.label.match(/\((GMT[+-]\d{1,2}:\d{2})\)\s*(.+)/);
      if (labelParts) {
        const offset = labelParts[1];
        const location = labelParts[2];
        zone.label = `${location} (${offset})`;
      }
      return zone;
    })
    .sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
  const canEdit = true
  return (
    <div className="my-6 mx-2 p-6 flex flex-1 flex-col justify-between bg-container-500 rounded">
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form className="relative" onSubmit={handleSubmit(handleUpdate)}>
          <div className="flex flex-1 flex-col">
            <div className="text-white text-lg font-semibold">Organization</div>
            <div className="flex flex-1 flex-col border-t mt-6 pt-8 pr-20 w-2/3 gap-8 border-t-gray-900">
              <div className="flex justify-between w-full">
                <div className="text-sm font-medium text-gray-500">Organization Name*</div>
                <div className="w-1/2">
                  <CustomInputBox
                    idname="clientName"
                    noMarginTop
                    isDisabled={!canEdit}
                    type="text"
                    validationObject={{ required: 'Please enter the organization name' }}
                  />
                </div>
              </div>
              <div className="flex justify-between w-full">
                <div className="text-sm font-medium text-gray-500">Timezone*</div>
                <div className="w-1/2">
                  <CustomDropdown
                    noMarginTop
                    isDisabled={!canEdit}
                    validationObject={{ required: 'Please select a timezone' }}
                    id="timeZone"
                    options={updatedTimeZones}
                  />
                </div>
              </div>
              <div className="flex justify-between w-full">
                <div className="text-sm font-medium text-gray-500">Org URL</div>
                <div className="w-1/2">
                  <CustomInputBox
                    idname="orgUrl"
                    noMarginTop
                    validationObject={{
                      pattern: RHFPatterns.url
                    }}
                    isDisabled={!canEdit}
                    actionElement={
                      <button
                        className="absolute right-2 bg-gray-800 flex items-center text-primary-200 top-2 bottom-2 text-xs px-2 rounded"
                        type="button"
                        onClick={() => {
                          watch('orgUrl');
                          toast.success('Copied to clipboard');
                        }}
                      >
                        <div className="material-icons-outlined text-xs">copy</div>
                        <div>Copy Link</div>
                      </button>
                    }
                  />
                </div>
              </div>
              <div className="flex justify-between w-full">
                <div className="text-sm font-medium text-gray-500">Industry*</div>
                <div className="w-1/2">
                  <CustomDropdown
                    noMarginTop
                    isDisabled={!canEdit}
                    validationObject={{ required: 'Please select an industry' }}
                    id="industry"
                    options={INDUSTRIES.map((industry) => ({ value: industry, label: industry }))}
                  />
                </div>
              </div>
              <div className="flex justify-between w-full">
                <div className="text-sm font-medium text-gray-500">Current Plan</div>
                <div className="w-1/2">
                  <div className="text-gray-300 text-sm font-medium">Professional</div>
                  {/* <div className="border border-primary-200 p-4 flex flex-col justify-between rounded mt-4">
                <div>
                  <div className="text-primary-200 text-xs">You are missing out on,</div>
                  <div className="text-primary-200 text-xs mt-3">Lorem ipsum</div>
                  <div className="text-primary-200 text-xs">Lorem ipsum</div>
                  <div className="text-primary-200 text-xs">Lorem ipsum</div>
                </div>
                <div className="flex justify-end mt-4">
                  <Button name="View Plans" size="sm" theme="primary-purple" />
                </div>
              </div> */}
                </div>
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex justify-end">
              <div className="flex items-center gap-5">
                {/* <Button disabled size="sm" theme="secondary-gray" name="Reset to Default" /> */}
                <Button
                  loading={loading}
                  buttonType="submit"
                  size="sm"
                  theme="primary"
                  name="Save Changes"
                />
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
}
